<!-- app/javascript/dashboard/components/CatalogModal.vue -->
<template>
  <div class="fixed inset-0 bg-black/[0.04] flex items-center justify-center z-[1000] backdrop-blur-sm" 
       @click.self="status === 'form' ? closeModal() : handleBackdropClick()">
    <div class="modal-content relative overflow-auto bg-n-alpha-3 shadow-xl rounded-xl rtl:text-right skip-context-menu"
         :class="[
           status === 'form' ? 'w-[37.5rem] max-h-[80vh]' : 'w-[64rem] max-h-[90vh]'
         ]">
      <div class="flex justify-between items-center p-4 border-b-2 border-n-slate-4 dark:border-n-slate-7">
        <h3 class="text-lg font-semibold m-0">
          <span v-if="status === 'form'">Adicionar Item ao Catálogo</span>
          <span v-else-if="status === 'product'">Adicionar Produto</span>
          <span v-else-if="status === 'service'">Adicionar Serviço</span>
        </h3>
        <button
          class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
          @click="closeModal">
          <span class="i-lucide-x flex-shrink-0"></span>
        </button>
      </div>

      <!-- Accessibility: Status announcements -->
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        class="sr-only"
      >
        {{ compressing ? 'Comprimindo imagens, aguarde...' : loading ? 'Carregando, aguarde...' : '' }}
      </div>

      <!-- Tela inicial de seleção -->
      <div v-if="status === 'form'" class="p-6">
        <div class="mb-5">
          <label class="block mb-3 text-sm font-medium">Selecione o tipo de item para adicionar:</label>
          <div class="grid grid-cols-3 gap-4">
            <!-- Produto -->
            <div 
              class="flex flex-col items-center justify-center p-6 border rounded-lg cursor-pointer transition-all h-28"
              :class="selectedType === 'product' ? 'border-n-brand' : 'border-n-weak hover:border-n-brand'"
              @click="selectType('product')"
            >
              <div class="mb-2 text-2xl">
                <i class="i-lucide-package"></i>
              </div>
              <h4 class="text-center text-base font-medium">Produto</h4>
            </div>
            
            <!-- Serviço -->
            <div 
              class="flex flex-col items-center justify-center p-6 border rounded-lg cursor-pointer transition-all h-28"
              :class="selectedType === 'service' ? 'border-n-brand' : 'border-n-weak hover:border-n-brand'"
              @click="selectType('service')"
            >
              <div class="mb-2 text-2xl">
                <i class="i-lucide-wrench"></i>
              </div>
              <h4 class="text-center text-base font-medium">Serviço</h4>
            </div>
          </div>
        </div>

        <div v-if="selectedType === 'product'" class="mb-5 mt-6">
          <p class="w-full text-slate-600 dark:text-slate-300">
            Adicione um produto ao seu catálogo com informações como nome, descrição, preço, categorias e imagens.
          </p>
        </div>

        <div v-if="selectedType === 'service'" class="mb-5 mt-6">
          <p class="w-full text-slate-600 dark:text-slate-300">
            Adicione um serviço ao seu catálogo com informações detalhadas, incluindo tipo, contato e localização.
          </p>
        </div>
        
        <p v-if="error && !error.includes('Arquivo muito grande') && !error.includes('imagem')" class="text-red-500 text-sm mt-4">{{ error }}</p>
      </div>

      <!-- Formulário de Produto -->
      <div v-else-if="status === 'product'" class="p-6">
        <div class="flex gap-6 lg:flex-row flex-col">
          <!-- Left Column - Input Fields -->
          <div class="flex-1">
            <!-- CAMPOS OBRIGATÓRIOS PRIMEIRO -->
            <div class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Informações Básicas <span class="text-red-500">*</span>
              </h4>
              
              <!-- Product Name -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Nome do produto <span class="text-red-500">*</span></label>
                <input type="text" v-model="productFormData.name" placeholder="Insira o nome do produto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" required />
              </div>

              <!-- Product Description -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Descrição do produto <span class="text-red-500">*</span></label>
                <textarea 
                  v-model="productFormData.description" 
                  placeholder="Insira a descrição do produto"
                  class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 resize-y" 
                  rows="4"
                  maxlength="2048"
                ></textarea>
                <div class="text-xs mt-1" :class="{'text-red-500': productDescriptionCharsRemaining < 100, 'text-n-slate-10': productDescriptionCharsRemaining >= 100}">
                  {{ productDescriptionCharsRemaining }} caracteres restantes
                </div>
              </div>

              <!-- Product Type -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tipo do produto <span class="text-red-500">*</span></label>
                <select v-model="productFormData.productType" required>
                  <option value="">Selecione o tipo</option>
                  <option value="Digital">Digital</option>
                  <option value="Físico">Físico</option>
                </select>
              </div>

              <!-- Product Value with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Valor do produto <span class="text-red-500">*</span></label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="productFormattedValue" @input="handleProductValueInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" required />
                </div>
              </div>
            </div>

            <!-- CAMPOS OPCIONAIS -->
            <div class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Informações Adicionais
              </h4>

              <!-- Discount Price with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço com desconto</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="productFormattedDiscountPrice" @input="handleProductDiscountPriceInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Reason -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Condição para desconto</label>
                <input type="text" v-model="productFormData.discountReason" placeholder="Explique quando a IA deve oferecer o desconto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="1000" />
              </div>

              <!-- Context URL -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">URL do produto</label>
                <input type="url" v-model="productFormData.contextUrl" placeholder="https://exemplo.com/produto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="500" />
                <div class="text-xs text-n-slate-10 mt-1 leading-normal">
                  Seus agentes de IA poderão ler desse link ou enviá-lo para clientes
                </div>
              </div>
            </div>

            <!-- Campos específicos para produtos físicos -->
            <div v-if="productFormData.productType === 'Físico'" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes do Produto Físico
              </h4>

              <!-- Weight -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Peso (kg)</label>
                <input type="number" v-model="productFormData.weightKg" placeholder="0.000" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       step="0.001" min="0" />
              </div>

              <!-- Dimensions -->
              <div>
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Dimensões (cm)</label>
                <div class="grid grid-cols-3 gap-2 mb-5">
                  <div>
                    <input type="number" v-model="productFormData.dimensionsCm.height" placeholder="Altura" 
                           class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                           min="0" step="0.1" />
                    <div class="text-xs text-n-slate-10 mt-1">Altura</div>
                  </div>
                  <div>
                    <input type="number" v-model="productFormData.dimensionsCm.width" placeholder="Largura" 
                           class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                           min="0" step="0.1" />
                    <div class="text-xs text-n-slate-10 mt-1">Largura</div>
                  </div>
                  <div>
                    <input type="number" v-model="productFormData.dimensionsCm.depth" placeholder="Profundidade" 
                           class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                           min="0" step="0.1" />
                    <div class="text-xs text-n-slate-10 mt-1">Profundidade</div>
                  </div>
                </div>
              </div>

              <!-- Color -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Cores</label>
                <input type="text" v-model="productFormData.color" placeholder="Cores do produto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="50" />
              </div>

              <!-- Ships To -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Envia para</label>
                <input type="text" v-model="productFormData.shipsTo" placeholder="Brasil, São Paulo..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="200" />
              </div>
            </div>

            <!-- Campos específicos para produtos digitais -->
            <div v-if="productFormData.productType === 'Digital'" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes do Produto Digital
              </h4>

              <!-- Formato do arquivo -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Formato do arquivo</label>
                <input type="text" v-model="productFormData.fileFormat" placeholder="PDF, MP4, ZIP..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="100" />
              </div>

              <!-- Tipo de licença -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tipo de licença</label>
                <input type="text" v-model="productFormData.licenseType" placeholder="Licença vitalícia, mensal..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="100" />
              </div>
            </div>
          </div>
          
          <!-- Right Column - Sticky Sidebar -->
          <div class="w-full lg:w-80 flex-shrink-0">
            <div class="lg:sticky lg:top-0 pt-5">
              <!-- Inbox Selection -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Inboxes do produto</label>
                <div class="relative inbox-dropdown">
                  <button
                    @click.stop="toggleInboxDropdown"
                    class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 flex justify-between items-center text-left"
                    type="button"
                  >
                    <div class="flex items-center overflow-hidden">
                      <div 
                        class="w-6 h-6 flex items-center justify-center rounded-full mr-2 flex-shrink-0"
                        :class="getSelectedInboxesDisplayClasses()"
                      >
                        <i :class="getSelectedInboxesDisplayIcon() + ' text-sm'"></i>
                      </div>

                      <span class="truncate">
                        {{ getSelectedInboxesDisplayText() }}
                      </span>
                    </div>
                    <i class="i-lucide-chevron-down ml-2 flex-shrink-0 transition-transform" :class="{ 'rotate-180': showInboxDropdown }"></i>
                  </button>
                  
                  <!-- Dropdown Options -->
                  <div v-if="showInboxDropdown" 
                       class="absolute z-50 top-full left-0 right-0 mt-1 border border-n-slate-3 rounded-md shadow-lg bg-white dark:bg-slate-800 overflow-hidden"
                       @click.stop>
                    <!-- Todos option -->
                    <div class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                         @click.stop="toggleInboxSelection('todos')">
                      <div class="flex items-center">
                        <input type="checkbox" 
                               :checked="isInboxSelected('todos')" 
                               @click.stop
                               @change.stop="toggleInboxSelection('todos')"
                               class="mr-3">
                        <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                          <i class="i-lucide-globe"></i>
                        </div>
                        <div>
                          <div class="text-sm font-medium">Todos</div>
                          <div class="text-xs text-slate-500">Disponível para todos os inboxes</div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Grouped Inboxes -->
                    <div class="max-h-60 overflow-y-auto">
                      <template v-for="(item, index) in groupedInboxOptions" :key="index">
                        <!-- Group Header -->
                        <div v-if="item.type === 'header'"
                             class="px-3 py-1 text-xs font-semibold bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-y border-slate-100 dark:border-slate-700 sticky top-0 z-10">
                          {{ item.label }}
                        </div>
                        
                        <!-- Inbox Option -->
                        <div v-else
                             class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                             @click.stop="toggleInboxSelection(String(item.inbox_id))">
                          <div class="flex items-center">
                            <input type="checkbox" 
                                   :checked="isInboxSelected(String(item.inbox_id))" 
                                   @click.stop
                                   @change.stop="toggleInboxSelection(String(item.inbox_id))"
                                   class="mr-3">
                            <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-grass-300 text-grass-900">
                              <i :class="getInboxTypeIcon(item.channel_type)"></i>
                            </div>
                            
                            <div>
                              <div class="text-sm font-medium">
                                {{ getInboxDisplayName(item) }}
                              </div>
                              <div v-if="item.integration_type === 'official'" class="text-xs text-grass-700 dark:text-grass-400">
                                API Oficial
                              </div>
                              <div v-else-if="item.integration_type === 'evolution'" class="text-xs text-grass-700 dark:text-grass-400">
                                Não Oficial
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="text-xs text-n-slate-10 mt-2 leading-normal">
                  Selecione os inboxes onde este produto estará disponível para os agentes de IA
                </div>
              </div>

              <!-- Product Category -->
              <IntegratedListView 
                v-model="productFormData.category" 
                title="Categoria do produto"
                input-placeholder="Digite a categoria e pressione Enter" 
                empty-message="Nenhuma categoria adicionada"
                required 
                class="mb-6"
              />

              <!-- Product Images -->
              <div>
                <label class="block mb-3 text-sm font-medium text-n-slate-12">Imagens do produto</label>
                <!-- Error message for file upload -->
                <p v-if="error && (error.includes('Arquivo muito grande') || error.includes('imagem'))" class="text-red-500 text-sm mb-3">
                  {{ error }}
                </p>
                <button
                  type="button"
                  @click="triggerFileUpload"
                  :disabled="loading || compressing"
                  :aria-busy="loading || compressing"
                  class="inline-flex items-center w-full gap-2 transition-all duration-200 ease-in-out border border-n-slate-4 dark:border-n-slate-7 rounded-lg outline-1 outline disabled:opacity-50 bg-n-background text-n-slate-12 hover:enabled:bg-n-slate-1 focus-visible:bg-n-slate-1 outline-transparent h-10 px-4 text-sm font-medium justify-center mb-3"
                >
                  <span v-if="loading || compressing" class="animate-spin i-lucide-loader flex-shrink-0"></span>
                  <span v-else class="i-lucide-upload flex-shrink-0"></span>
                  <span>{{ compressing ? 'Comprimindo imagens...' : loading ? 'Enviando imagens...' : 'Enviar imagens' }}</span>
                </button>
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  multiple
                  @change="handleFileUpload"
                  class="hidden"
                />
              </div>
              <IntegratedImageView 
                v-model="productFormData.images" 
                title=""
                input-placeholder="Ou digite a URL da imagem" 
                empty-message="Nenhuma imagem adicionada"
                :modal-loading="loading"
              />
            </div>
          </div>
        </div>

        <p v-if="error && !error.includes('Arquivo muito grande') && !error.includes('imagem')" class="text-red-500 text-sm mt-4">{{ error }}</p>
      </div>

      <!-- Formulário de Serviço -->
      <div v-else-if="status === 'service'" class="p-6">
        <div class="flex gap-6 lg:flex-row flex-col">
          <!-- Left Column - Input Fields -->
          <div class="flex-1">
            <!-- CAMPOS OBRIGATÓRIOS PRIMEIRO -->
            <div class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Informações Básicas <span class="text-red-500">*</span>
              </h4>
              
              <!-- Service Name -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Nome do serviço <span class="text-red-500">*</span></label>
                <input type="text" v-model="serviceFormData.name" placeholder="Insira o nome do serviço" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" required />
              </div>

              <!-- Service Description -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Descrição do serviço <span class="text-red-500">*</span></label>
                <textarea 
                  v-model="serviceFormData.description" 
                  placeholder="Insira a descrição do serviço"
                  class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 resize-y" 
                  rows="4"
                  maxlength="2048"
                ></textarea>
                <div class="text-xs mt-1" :class="{'text-red-500': serviceDescriptionCharsRemaining < 100, 'text-n-slate-10': serviceDescriptionCharsRemaining >= 100}">
                  {{ serviceDescriptionCharsRemaining }} caracteres restantes
                </div>
              </div>

              <!-- Service Type -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tipo de serviço <span class="text-red-500">*</span></label>
                <select v-model="serviceFormData.type" 
                        required>
                  <option value="">Selecione o tipo</option>
                  <option value="aluguel">Aluguel</option>
                  <option value="delivery">Delivery</option>
                  <option value="consulta">Consulta</option>
                  <option value="tratamento">Tratamento</option>
                  <option value="assinatura">Assinatura</option>
                </select>
              </div>
            </div>

            <!-- CAMPOS OPCIONAIS -->
            <div class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Informações Adicionais
              </h4>

              <!-- Link do serviço -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Link do serviço</label>
                <input type="url" v-model="serviceFormData.link" placeholder="https://exemplo.com/servico" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="500" />
                <div class="text-xs text-n-slate-10 mt-1 leading-normal">
                  Seus agentes de IA poderão ler desse link ou enviá-lo para clientes
                </div>
              </div>

              <!-- Endereço -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Endereço</label>
                <input type="text" v-model="serviceFormData.address" placeholder="Rua, número, bairro, cidade..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" />
              </div>
            </div>

            <!-- Campos específicos para delivery -->
            <div v-if="serviceFormData.type === 'delivery'" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes do Delivery
              </h4>

              <!-- Service Value with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Valor do serviço</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="serviceFormattedValue" @input="handleServiceValueInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Price with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço com desconto</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="serviceFormattedDiscountPrice" @input="handleServiceDiscountPriceInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Reason -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Condição para desconto</label>
                <input type="text" v-model="serviceFormData.discountReason" placeholder="Explique quando a IA deve oferecer o desconto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="1000" />
              </div>

              <!-- Distância máxima -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Distância de atendimento</label>
                <input type="text" v-model="serviceFormData.maxDistance" placeholder="Birigui, Araçatuba, Coroados..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="255" />
              </div>

              <!-- Preço fixo ou base toggle -->
              <div class="mb-5">
                <label class="flex items-center cursor-pointer">
                  <input type="checkbox" v-model="serviceFormData.isFixedPrice" 
                         class="mr-3 w-4 h-4 text-n-brand bg-n-background border-n-slate-4 rounded focus:ring-n-brand dark:focus:ring-n-brand dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600" />
                  <span class="text-sm font-medium text-n-slate-12">Preço fixo?</span>
                </label>
              </div>

              <!-- Tempo estimado em minutos -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tempo estimado (minutos)</label>
                <input type="number" v-model="serviceFormData.estimatedMinutes" placeholder="30" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       min="1" max="480" />
              </div>
            </div>

            <!-- Campos específicos para consulta, tratamento e aluguel -->
            <div v-if="serviceFormData.type === 'consulta' || serviceFormData.type === 'tratamento' || serviceFormData.type === 'aluguel'" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes do {{ serviceFormData.type === 'consulta' ? 'Consulta' : serviceFormData.type === 'tratamento' ? 'Tratamento' : 'Aluguel' }}
              </h4>

              <!-- Valor por hora toggle -->
              <div class="mb-5">
                <label class="flex items-center cursor-pointer">
                  <input type="checkbox" v-model="serviceFormData.isHourlyRate" 
                         class="mr-3 w-4 h-4 text-n-brand bg-n-background border-n-slate-4 rounded focus:ring-n-brand dark:focus:ring-n-brand dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600" />
                  <span class="text-sm font-medium text-n-slate-12">Valor por hora?</span>
                </label>
              </div>

              <!-- Service Value with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">
                  {{ serviceFormData.isHourlyRate ? 'Valor por hora' : 'Valor do serviço' }}
                </label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="serviceFormattedValue" @input="handleServiceValueInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Price for consulta/tratamento/aluguel -->
              <div v-if="serviceFormData.type === 'consulta' || serviceFormData.type === 'tratamento' || serviceFormData.type === 'aluguel'" class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço com desconto</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="serviceFormattedDiscountPrice" @input="handleServiceDiscountPriceInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Discount Reason for consulta/tratamento/aluguel -->
              <div v-if="serviceFormData.type === 'consulta' || serviceFormData.type === 'tratamento' || serviceFormData.type === 'aluguel'" class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Condição para desconto</label>
                <input type="text" v-model="serviceFormData.discountReason" placeholder="Explique quando a IA deve oferecer o desconto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="1000" />
              </div>

              <!-- Specialty field for Consulta and Tratamento -->
              <div v-if="serviceFormData.type === 'consulta' || serviceFormData.type === 'tratamento'" class="mb-2">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12" for="service-specialty-select">Especialidade</label>
                <select id="service-specialty-select" v-model="serviceFormData.specialty">
                  <option value="">Nenhuma selecionada</option>
                  <option v-for="specialty in specialties" :key="specialty.id" :value="specialty.id">
                    {{ specialty.name }}
                  </option>
                </select>
              </div>

              <!-- Duration field for Consulta and Tratamento -->
              <div v-if="serviceFormData.type === 'consulta' || serviceFormData.type === 'tratamento'" class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tempo de duração (minutos)</label>
                <input type="number" v-model="serviceFormData.durationMinutes" placeholder="60" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       min="1" />
                <div class="text-xs text-n-slate-10 mt-1 leading-normal">
                  Duração do atendimento em minutos. Padrão 60 minutos caso não especificado.
                </div>
              </div>

              <!-- Marcar com antecedência -->
              <div v-if="serviceFormData.type === 'consulta' || serviceFormData.type === 'tratamento' || serviceFormData.type === 'aluguel'" class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Marcar com antecedência (dias)</label>
                <input type="number" v-model="serviceFormData.advanceBookingDays" placeholder="1" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       min="0" max="365" />
                <div class="text-xs text-n-slate-10 mt-1 leading-normal">
                  Quantos dias de antecedência são necessários para o agendamento
                </div>
              </div>
            </div>

            <!-- Campos específicos para assinatura -->
            <div v-if="serviceFormData.type === 'assinatura'" class="mb-8">
              <h4 class="text-base font-semibold text-n-slate-12 mb-4 pb-2 border-b border-n-slate-3">
                Detalhes da Assinatura
              </h4>

              <!-- Preço do plano with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço do plano</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="serviceFormattedValue" @input="handleServiceValueInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Preço de desconto with prefix "R$" -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Preço de desconto</label>
                <div class="flex items-start">
                  <span class="px-3 py-2.5 bg-n-slate-4 dark:bg-n-slate-7 border border-n-slate-4 dark:border-n-slate-7 border-r-0 rounded-lg text-sm">R$</span>
                  <input type="text" v-model="serviceFormattedDiscountPrice" @input="handleServiceDiscountPriceInput" placeholder="0,00"
                         class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" />
                </div>
              </div>

              <!-- Condição para desconto -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Condição para desconto</label>
                <input type="text" v-model="serviceFormData.discountReason" placeholder="Explique quando a IA deve oferecer o desconto" 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="1000" />
              </div>

              <!-- Tipo de plano -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Tipo de plano</label>
                <input type="text" v-model="serviceFormData.planType" placeholder="Anual, mensal..." 
                       class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                       maxlength="100" />
              </div>
            </div>
          </div>
          
          <!-- Right Column - Sticky Sidebar -->
          <div class="w-full lg:w-80 flex-shrink-0">
            <div class="lg:sticky lg:top-0 pt-5">
              <!-- Inbox Selection -->
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Inboxes do serviço</label>
                <div class="relative inbox-dropdown">
                  <button
                    @click.stop="toggleInboxDropdown"
                    class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 flex justify-between items-center text-left"
                    type="button"
                  >
                    <div class="flex items-center overflow-hidden">
                      <div 
                        class="w-6 h-6 flex items-center justify-center rounded-full mr-2 flex-shrink-0"
                        :class="getSelectedInboxesDisplayClasses()"
                      >
                        <i :class="getSelectedInboxesDisplayIcon() + ' text-sm'"></i>
                      </div>

                      <span class="truncate">
                        {{ getSelectedInboxesDisplayText() }}
                      </span>
                    </div>
                    <i class="i-lucide-chevron-down ml-2 flex-shrink-0 transition-transform" :class="{ 'rotate-180': showInboxDropdown }"></i>
                  </button>
                  
                  <!-- Dropdown Options -->
                  <div v-if="showInboxDropdown" 
                       class="absolute z-50 top-full left-0 right-0 mt-1 border border-n-slate-3 rounded-md shadow-lg bg-white dark:bg-slate-800 overflow-hidden"
                       @click.stop>
                    <!-- Todos option -->
                    <div class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                         @click.stop="toggleInboxSelection('todos')">
                      <div class="flex items-center">
                        <input type="checkbox" 
                               :checked="isInboxSelected('todos')" 
                               @click.stop
                               @change.stop="toggleInboxSelection('todos')"
                               class="mr-3">
                        <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                          <i class="i-lucide-globe"></i>
                        </div>
                        <div>
                          <div class="text-sm font-medium">Todos</div>
                          <div class="text-xs text-slate-500">Disponível para todos os inboxes</div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Grouped Inboxes -->
                    <div class="max-h-60 overflow-y-auto">
                      <template v-for="(item, index) in groupedInboxOptions" :key="index">
                        <!-- Group Header -->
                        <div v-if="item.type === 'header'"
                             class="px-3 py-1 text-xs font-semibold bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-y border-slate-100 dark:border-slate-700 sticky top-0 z-10">
                          {{ item.label }}
                        </div>
                        
                        <!-- Inbox Option -->
                        <div v-else
                             class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                             @click.stop="toggleInboxSelection(String(item.inbox_id))">
                          <div class="flex items-center">
                            <input type="checkbox" 
                                   :checked="isInboxSelected(String(item.inbox_id))" 
                                   @click.stop
                                   @change.stop="toggleInboxSelection(String(item.inbox_id))"
                                   class="mr-3">
                            <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-grass-300 text-grass-900">
                              <i :class="getInboxTypeIcon(item.channel_type)"></i>
                            </div>
                            
                            <div>
                              <div class="text-sm font-medium">
                                {{ getInboxDisplayName(item) }}
                              </div>
                              <div v-if="item.integration_type === 'official'" class="text-xs text-grass-700 dark:text-grass-400">
                                API Oficial
                              </div>
                              <div v-else-if="item.integration_type === 'evolution'" class="text-xs text-grass-700 dark:text-grass-400">
                                Não Oficial
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="text-xs text-n-slate-10 mt-2 leading-normal">
                  Selecione os inboxes onde este serviço estará disponível para os agentes de IA
                </div>
              </div>

              <!-- Service Category -->
              <IntegratedListView 
                v-model="serviceFormData.category" 
                title="Categoria do serviço"
                input-placeholder="Digite a categoria e pressione Enter" 
                empty-message="Nenhuma categoria adicionada"
                required 
                class="mb-6"
              />

              <!-- Service Images -->
              <div>
                <label class="block mb-3 text-sm font-medium text-n-slate-12">Imagens do serviço</label>
                <!-- Error message for file upload -->
                <p v-if="error && (error.includes('Arquivo muito grande') || error.includes('imagem'))" class="text-red-500 text-sm mb-3">
                  {{ error }}
                </p>
                <button
                  type="button"
                  @click="triggerFileUpload"
                  :disabled="loading || compressing"
                  :aria-busy="loading || compressing"
                  class="inline-flex items-center w-full gap-2 transition-all duration-200 ease-in-out border border-n-slate-4 dark:border-n-slate-7 rounded-lg outline-1 outline disabled:opacity-50 bg-n-background text-n-slate-12 hover:enabled:bg-n-slate-1 focus-visible:bg-n-slate-1 outline-transparent h-10 px-4 text-sm font-medium justify-center mb-3"
                >
                  <span v-if="loading || compressing" class="animate-spin i-lucide-loader flex-shrink-0"></span>
                  <span v-else class="i-lucide-upload flex-shrink-0"></span>
                  <span>{{ compressing ? 'Comprimindo imagens...' : loading ? 'Enviando imagens...' : 'Enviar imagens' }}</span>
                </button>
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  multiple
                  @change="handleFileUpload"
                  class="hidden"
                />
              </div>
              <IntegratedImageView 
                v-model="serviceFormData.images" 
                title=""
                input-placeholder="Ou digite a URL da imagem" 
                empty-message="Nenhuma imagem adicionada"
                :modal-loading="loading"
              />
            </div>
          </div>
        </div>

        <p v-if="error && !error.includes('Arquivo muito grande') && !error.includes('imagem')" class="text-red-500 text-sm mt-4">{{ error }}</p>
      </div>

      <!-- Footer para seleção inicial -->
      <div v-if="status === 'form'" class="flex justify-end gap-3 p-4 border-t-2 border-n-slate-4 dark:border-n-slate-7 sticky bottom-0 bg-n-alpha-3">
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
          @click="closeModal" :disabled="loading">
          Cancelar
        </button>
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
          @click="proceedToForm"
          :disabled="!selectedType">
          <span>Continuar</span>
        </button>
      </div>

      <!-- Footer para formulário de produto -->
      <div v-else-if="status === 'product'" class="flex justify-end gap-3 p-4 border-t-2 border-n-slate-4 dark:border-n-slate-7 sticky bottom-0 bg-n-alpha-3">
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center mr-auto"
          @click="backToSelection" :disabled="loading">
          Voltar
        </button>
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
          @click="closeModal" :disabled="loading">
          Cancelar
        </button>
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
          @click="saveProduct" :disabled="!isProductFormValid || loading">
          <span v-if="loading" class="animate-spin i-lucide-loader"></span>
          <span>{{ loading ? 'Adicionando...' : 'Adicionar produto' }}</span>
        </button>
      </div>

      <!-- Footer para formulário de serviço -->
      <div v-else-if="status === 'service'" class="flex justify-end gap-3 p-4 border-t-2 border-n-slate-4 dark:border-n-slate-7 sticky bottom-0 bg-n-alpha-3">
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center mr-auto"
          @click="backToSelection" :disabled="loading">
          Voltar
        </button>
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
          @click="closeModal" :disabled="loading">
          Cancelar
        </button>
        <button type="button"
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
          @click="saveService" :disabled="!isServiceFormValid || loading">
          <span v-if="loading" class="animate-spin i-lucide-loader"></span>
          <span>{{ loading ? 'Adicionando...' : 'Adicionar serviço' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import IntegratedListView from './IntegratedListView.vue';
import IntegratedImageView from '@/dashboard/components/IntegratedImageView.vue';
import wootAPI from '@/v3/api/apiClient';

export default {
  name: 'CatalogModal',
  components: { IntegratedListView, IntegratedImageView },
  props: {
    connectedInboxes: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'save', 'success'],
  setup(props, { emit }) {
    const store = useStore();
    const status = ref('form');
    const selectedType = ref('');
    const loading = ref(false);
    const error = ref(null);
    const fileInput = ref(null);
    const compressing = ref(false);
    
    // Inbox selection state
    const inboxOptions = ref([]);
    const showInboxDropdown = ref(false);
    
    // Specialties for service form
    const specialties = ref([]);

    // Dados do formulário de produto
    const productFormData = ref({
      name: '',
      description: '',
      price: null,
      discountPrice: null,
      discountReason: '',
      contextUrl: '',
      productType: '',
      selectedInboxes: ['todos'],
      category: [],
      images: [],
      // Campos específicos para produtos físicos
      weightKg: null,
      dimensionsCm: {
        height: null,
        width: null,
        depth: null
      },
      color: '',
      shipsTo: '',
      // Campos específicos para produtos digitais
      fileFormat: '',
      licenseType: ''
    });

    // Dados do formulário de serviço
    const serviceFormData = ref({
      name: '',
      description: '',
      price: null,
      discountPrice: null,
      discountReason: '',
      link: '',
      address: '',
      type: '',
      selectedInboxes: ['todos'],
      category: [],
      images: [],
      // Campos específicos para aluguel e delivery
      maxDistance: '',
      isFixedPrice: false,
      estimatedMinutes: null,
      // Campos específicos para consulta e tratamento
      isHourlyRate: false,
      hasFixedDuration: false,
      durationMinutes: null,
      advanceBookingDays: null,
      specialty: '',
      // Campos específicos para assinatura
      planType: ''
    });

    const productFormattedValue = ref('');
    const productFormattedDiscountPrice = ref('');
    const serviceFormattedValue = ref('');
    const serviceFormattedDiscountPrice = ref('');

    // Computadores para caracteres restantes
    const productDescriptionCharsRemaining = computed(() => {
      const MAX_DESCRIPTION_LENGTH = 2048;
      return MAX_DESCRIPTION_LENGTH - (productFormData.value.description?.length || 0);
    });

    const serviceDescriptionCharsRemaining = computed(() => {
      const MAX_DESCRIPTION_LENGTH = 2048;
      return MAX_DESCRIPTION_LENGTH - (serviceFormData.value.description?.length || 0);
    });

    const formatToBRL = (value) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2
      }).format(value);
    };

    // Handlers para produto
    const handleProductValueInput = (event) => {
      error.value = null;
      const input = event.target.value;
      const numeric = input.replace(/\D/g, '');
      const value = Number(numeric) / 100;

      productFormData.value.price = value;
      productFormattedValue.value = formatToBRL(value).replace('R$', '').trim();
    };

    const handleProductDiscountPriceInput = (event) => {
      error.value = null;
      const input = event.target.value;
      const numeric = input.replace(/\D/g, '');
      const value = Number(numeric) / 100;

      productFormData.value.discountPrice = value;
      productFormattedDiscountPrice.value = formatToBRL(value).replace('R$', '').trim();
    };

    // Handlers para serviço
    const handleServiceValueInput = (event) => {
      error.value = null;
      const input = event.target.value;
      const numeric = input.replace(/\D/g, '');
      const value = Number(numeric) / 100;

      serviceFormData.value.price = value;
      serviceFormattedValue.value = formatToBRL(value).replace('R$', '').trim();
    };

    const handleServiceDiscountPriceInput = (event) => {
      error.value = null;
      const input = event.target.value;
      const numeric = input.replace(/\D/g, '');
      const value = Number(numeric) / 100;

      serviceFormData.value.discountPrice = value;
      serviceFormattedDiscountPrice.value = formatToBRL(value).replace('R$', '').trim();
    };

    // Watchers para produto
    watch(() => productFormData.value.price, (newVal) => {
      if (newVal !== null) {
        productFormattedValue.value = formatToBRL(newVal).replace('R$', '').trim();
      }
    });

    watch(() => productFormData.value.discountPrice, (newVal) => {
      if (newVal !== null) {
        productFormattedDiscountPrice.value = formatToBRL(newVal).replace('R$', '').trim();
      }
    });

    watch(() => serviceFormData.value.price, (newVal) => {
      if (newVal !== null) {
        serviceFormattedValue.value = formatToBRL(newVal).replace('R$', '').trim();
      }
    });

    watch(() => serviceFormData.value.discountPrice, (newVal) => {
      if (newVal !== null) {
        serviceFormattedDiscountPrice.value = formatToBRL(newVal).replace('R$', '').trim();
      }
    });

    // Watchers para limites de caracteres
    watch(() => productFormData.value.description, (newValue) => {
      if (newValue && newValue.length >= 2048) {
        error.value = "A descrição atingiu o limite máximo de 2048 caracteres.";
      } else if (error.value === "A descrição atingiu o limite máximo de 2048 caracteres.") {
        error.value = null;
      }
    });

    watch(() => serviceFormData.value.description, (newValue) => {
      if (newValue && newValue.length >= 2048) {
        error.value = "A descrição atingiu o limite máximo de 2048 caracteres.";
      } else if (error.value === "A descrição atingiu o limite máximo de 2048 caracteres.") {
        error.value = null;
      }
    });

    // Validações de formulário
    const isProductFormValid = computed(() => {
      const basicValidation = (
        productFormData.value.name && 
        productFormData.value.description && 
        productFormData.value.description.length <= 2048 &&
        productFormData.value.price !== null &&
        productFormData.value.productType
      );

      return basicValidation;
    });

    const isServiceFormValid = computed(() => {
      const basicValidation = (
        serviceFormData.value.name &&
        serviceFormData.value.description &&
        serviceFormData.value.description.length <= 2048 &&
        serviceFormData.value.type
      );

      return basicValidation;
    });

    // Função para verificar se todos os campos estão vazios
    const areAllFieldsEmpty = () => {
      if (status.value === 'product') {
        const physicalFieldsEmpty = productFormData.value.productType !== 'Físico' || (
          (productFormData.value.weightKg === null || productFormData.value.weightKg === 0) &&
          (productFormData.value.dimensionsCm.height === null || productFormData.value.dimensionsCm.height === 0) &&
          (productFormData.value.dimensionsCm.width === null || productFormData.value.dimensionsCm.width === 0) &&
          (productFormData.value.dimensionsCm.depth === null || productFormData.value.dimensionsCm.depth === 0) &&
          productFormData.value.color.trim() === '' &&
          productFormData.value.shipsTo.trim() === ''
        );

        const digitalFieldsEmpty = productFormData.value.productType !== 'Digital' || (
          productFormData.value.fileFormat.trim() === '' &&
          productFormData.value.licenseType.trim() === ''
        );

        return (
          productFormData.value.name.trim() === '' &&
          productFormData.value.description.trim() === '' &&
          (productFormData.value.price === null || productFormData.value.price === 0) &&
          (productFormData.value.discountPrice === null || productFormData.value.discountPrice === 0) &&
          productFormData.value.contextUrl.trim() === '' &&
          productFormData.value.productType === '' &&
          productFormData.value.selectedInboxes.length === 0 &&
          productFormData.value.category.length === 0 &&
          productFormData.value.images.length === 0 &&
          physicalFieldsEmpty &&
          digitalFieldsEmpty
        );
      } else if (status.value === 'service') {
        const deliveryFieldsEmpty = serviceFormData.value.type !== 'delivery' || (
          serviceFormData.value.maxDistance.trim() === '' &&
          (serviceFormData.value.estimatedMinutes === null || serviceFormData.value.estimatedMinutes === 0)
        );

        const appointmentFieldsEmpty = (serviceFormData.value.type !== 'consulta' && serviceFormData.value.type !== 'tratamento' && serviceFormData.value.type !== 'aluguel') || (
          (serviceFormData.value.advanceBookingDays === null || serviceFormData.value.advanceBookingDays === 0)
        );

        return (
          serviceFormData.value.name.trim() === '' &&
          serviceFormData.value.description.trim() === '' &&
          (serviceFormData.value.price === null || serviceFormData.value.price === 0) &&
          (serviceFormData.value.discountPrice === null || serviceFormData.value.discountPrice === 0) &&
          serviceFormData.value.discountReason.trim() === '' &&
          serviceFormData.value.link.trim() === '' &&
          serviceFormData.value.address.trim() === '' &&
          serviceFormData.value.type === '' &&
          serviceFormData.value.category.length === 0 &&
          serviceFormData.value.images.length === 0 &&
          deliveryFieldsEmpty &&
          appointmentFieldsEmpty
        );
      }
      return true;
    };

    // Função para handle de clique no backdrop
    const handleBackdropClick = () => {
      if (areAllFieldsEmpty()) {
        closeModal();
      }
    };

    const selectType = (type) => {
      selectedType.value = type;
      error.value = null;
    };

    const proceedToForm = () => {
      if (selectedType.value === 'product') {
        status.value = 'product';
      } else if (selectedType.value === 'service') {
        status.value = 'service';
      }
    };

    const backToSelection = () => {
      status.value = 'form';
      error.value = null;
    };

    const closeModal = () => {
      emit('close');
    };
    
    // Inbox functionality
    const groupedInboxOptions = computed(() => {
      const result = [];
      
      // Get all the inbox options
      const inboxes = inboxOptions.value || [];
      
      // Extract channel types for grouping
      const whatsappEvolution = [];
      const whatsappOfficial = [];
      const otherChannels = [];
      
      inboxes.forEach(inbox => {
        // Use name to identify the type since integration_type might not be available
        const name = (inbox.inbox_name || inbox.name || '').toLowerCase();
        
        // Official API inboxes will have cloud_api or official in the name or a provider property
        if (inbox.integration_type === 'official' || 
            name.includes('api oficial') ||
            name.includes('cloud') ||
            name.includes('official')) {
          whatsappOfficial.push({...inbox, integration_type: 'official'});
        }
        // Evolution inboxes will have evolution in the name
        else if (inbox.integration_type === 'evolution' || name.includes('evolution') || name.includes('unofficial')) {
          whatsappEvolution.push({...inbox, integration_type: 'evolution'});
        }
        // Otherwise, put it in other channels
        else {
          otherChannels.push(inbox);
        }
      });
      
      // Add evolution group
      if (whatsappEvolution.length > 0) {
        result.push({ type: 'header', label: 'WHATSAPP NÃO OFICIAL' });
        result.push(...whatsappEvolution);
      }
      
      // Add official group
      if (whatsappOfficial.length > 0) {
        result.push({ type: 'header', label: 'WHATSAPP API OFICIAL' });
        result.push(...whatsappOfficial);
      }
      
      // Add other channels group
      if (otherChannels.length > 0) {
        result.push({ type: 'header', label: 'OUTROS CANAIS' });
        result.push(...otherChannels);
      }
      
      return result;
    });
    
    // Helper function to get the appropriate icon for an inbox type
    const getInboxTypeIcon = (type) => {
      switch (type?.toLowerCase()) {
        case 'whatsapp':
          return 'i-lucide-message-circle';
        case 'facebook':
          return 'i-lucide-facebook';
        case 'twitter':
          return 'i-lucide-twitter';
        case 'email':
          return 'i-lucide-mail';
        case 'web':
          return 'i-lucide-globe';
        case 'telegram':
          return 'i-lucide-send';
        default:
          return 'i-lucide-message-square';
      }
    };
    
    // Toggle dropdown
    const toggleInboxDropdown = () => {
      showInboxDropdown.value = !showInboxDropdown.value;
    };
    
    // Load inboxes from props
    const loadInboxes = () => {
      // Use the connected inboxes passed as prop
      inboxOptions.value = props.connectedInboxes || [];
      console.log('Loaded inboxes from props:', inboxOptions.value.length);
    };
    
    // Load specialties
    const loadSpecialties = async () => {
      try {
        const headers = getAuthHeaders();
        const response = await wootAPI.get('/api/v1/specialties?per_page=100', { headers });
        specialties.value = response.data.data;
      } catch (err) {
        console.error('Falha ao carregar especialidades:', err);
        // Don't show error to user as this is optional
        specialties.value = [];
      }
    };
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.inbox-dropdown') && showInboxDropdown.value) {
        showInboxDropdown.value = false;
      }
    };
    
    // Multi-select inbox functionality
    const isInboxSelected = (inboxId) => {
      const selectedInboxes = status.value === 'product' 
        ? productFormData.value.selectedInboxes 
        : serviceFormData.value.selectedInboxes;
      return selectedInboxes.includes(inboxId);
    };
    
    const toggleInboxSelection = (inboxId) => {
      const isProductForm = status.value === 'product';
      const formData = isProductForm ? productFormData.value : serviceFormData.value;
      const currentSelection = [...formData.selectedInboxes];
      
      if (inboxId === 'todos') {
        // If selecting "Todos", clear all individual selections
        if (currentSelection.includes('todos')) {
          // Don't allow deselecting if it's the only selection
          if (currentSelection.length === 1) {
            // Show error or just return without changing
            error.value = 'Deve selecionar pelo menos um inbox';
            return;
          }
          // If "Todos" is already selected, deselect it
          formData.selectedInboxes = [];
        } else {
          // Select "Todos" and clear individual selections
          formData.selectedInboxes = ['todos'];
        }
      } else {
        // Individual inbox selection
        const todosIndex = currentSelection.indexOf('todos');
        const inboxIndex = currentSelection.indexOf(inboxId);
        
        // Remove "Todos" if it was selected (since we're selecting specific inboxes)
        if (todosIndex !== -1) {
          currentSelection.splice(todosIndex, 1);
        }
        
        if (inboxIndex === -1) {
          // Add inbox to selection
          currentSelection.push(inboxId);
        } else {
          // Don't allow removing if it's the last selection
          if (currentSelection.length === 1) {
            error.value = 'Deve selecionar pelo menos um inbox';
            return;
          }
          // Remove inbox from selection
          currentSelection.splice(inboxIndex, 1);
        }
        
        formData.selectedInboxes = currentSelection;
      }
      
      // Clear error if selection is valid
      if (formData.selectedInboxes.length > 0) {
        error.value = null;
      }
    };
    
    const getInboxName = (inboxId) => {
      if (inboxId === 'todos') return 'Todos';
      
      // First try to get display_name from store
      const displayName = store.getters['inboxMetadata/getInboxDisplayName'](inboxId);
      if (displayName) return displayName;
      
      // Fallback to inbox_name from inboxOptions
      const inbox = inboxOptions.value.find(inbox => 
        String(inbox.inbox_id) === inboxId
      );
      
      return inbox ? inbox.inbox_name : 'Inbox desconhecido';
    };
    
    const getInboxDisplayName = (inbox) => {
      // First try to get display_name from store
      const displayName = store.getters['inboxMetadata/getInboxDisplayName'](inbox.inbox_id || inbox.id);
      if (displayName) return displayName;
      
      // Fallback to inbox_name or name
      return inbox.inbox_name || inbox.name || `Inbox ${inbox.inbox_id || inbox.id}`;
    };
    
    const getSelectedInboxesDisplayText = () => {
      const selected = status.value === 'product' 
        ? productFormData.value.selectedInboxes 
        : serviceFormData.value.selectedInboxes;
      
      if (selected.includes('todos')) {
        return 'Todos';
      } else if (selected.length === 0) {
        return 'Selecionar inboxes';
      } else if (selected.length === 1) {
        return getInboxName(selected[0]);
      } else {
        return `${selected.length} inboxes selecionados`;
      }
    };
    
    const getSelectedInboxesDisplayIcon = () => {
      const selected = status.value === 'product' 
        ? productFormData.value.selectedInboxes 
        : serviceFormData.value.selectedInboxes;
      
      if (selected.includes('todos')) {
        return 'i-lucide-globe';
      } else if (selected.length === 0) {
        return 'i-lucide-inbox';
      } else if (selected.length === 1) {
        const inbox = inboxOptions.value.find(inbox => 
          String(inbox.inbox_id) === selected[0]
        );
        return inbox ? getInboxTypeIcon(inbox.channel_type) : 'i-lucide-inbox';
      } else {
        return 'i-lucide-layers';
      }
    };
    
    const getSelectedInboxesDisplayClasses = () => {
      const selected = status.value === 'product' 
        ? productFormData.value.selectedInboxes 
        : serviceFormData.value.selectedInboxes;
      
      if (selected.includes('todos')) {
        return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
      } else if (selected.length === 0) {
        return 'bg-n-slate-2 text-n-slate-11';
      } else if (selected.length === 1) {
        const inbox = inboxOptions.value.find(inbox => 
          String(inbox.inbox_id) === selected[0]
        );
        if (!inbox) return 'bg-n-slate-2 text-n-slate-11';
        
        if (inbox.integration_type === 'official') {
          return 'bg-grass-300 text-grass-900';
        } else if (inbox.integration_type === 'evolution') {
          return 'bg-grass-300 text-grass-900';
        } else {
          return 'bg-n-slate-2 text-n-slate-11';
        }
      } else {
        return 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400';
      }
    };

    // Funções de autenticação
    const getCookie = (name) => {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? decodeURIComponent(match[2]) : null;
    };

    const getAuthHeaders = () => {
      const cookie = getCookie('cw_d_session_info');
      if (!cookie) {
        console.error('Cookie cw_d_session_info não encontrado.');
        return {};
      }
      try {
        const sessionInfo = JSON.parse(cookie);
        return {
          'access-token': sessionInfo['access-token'],
          'client': sessionInfo['client'],
          'uid': sessionInfo['uid']
        };
      } catch (e) {
        console.error('Erro tentando fazer parsing cw_d_session_info cookie:', e);
        return {};
      }
    };

    const uploadTemporaryImages = async (images) => {
      const headers = getAuthHeaders();
      if (!headers['access-token']) {
        throw new Error('Usuário não autenticado.');
      }

      const uploadPromises = images.map(async (image) => {
        if (!image.isTemporary) {
          // Already uploaded image, return as-is
          const urlString = typeof image === 'string' ? image : image.url;

          // Rejeita imagens em formato base64
          if (urlString?.startsWith('data:')) {
            throw new Error('Formato inválido. Por favor, utilize outro formato.');
          }

          return urlString;
        }

        console.log(`🚀 Uploading image to server: ${image.file.name}, Size: ${(image.file.size / 1024 / 1024).toFixed(2)}MB`);

        // Upload temporary image to Firebase
        const formData = new FormData();
        formData.append('file', image.file);

        try {
          const response = await wootAPI.post('/api/v1/catalogs/upload_image', formData, {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          });

          console.log(`✅ Upload successful for ${image.file.name}:`, response.data);

          return response.data.url;
        } catch (err) {
          console.error(`❌ Upload failed for ${image.file.name}:`, err);
          
          // Check for specific file size error from server
          if (err.response?.data?.error && err.response.data.error.includes('muito grande')) {
            throw new Error(err.response.data.error);
          }
          throw err;
        }
      });

      return await Promise.all(uploadPromises);
    };

    const saveProduct = debounce(async () => {
      if (!isProductFormValid.value) return;

      loading.value = true;
      error.value = null;

      const headers = getAuthHeaders();
      if (!headers['access-token']) {
        error.value = 'Usuário não autenticado.';
        loading.value = false;
        return;
      }

      const categoryNames = productFormData.value.category.map(item =>
        typeof item === 'string' ? item : item.name
      );

      // Upload temporary images to Firebase before saving
      const imageUrls = await uploadTemporaryImages(productFormData.value.images);

      // Prepare dimensions array for physical products
      let dimensionsArray = null;
      if (productFormData.value.productType === 'Físico') {
        const { height, width, depth } = productFormData.value.dimensionsCm;
        if (height || width || depth) {
          dimensionsArray = [height || 0, width || 0, depth || 0];
        }
      }

      const productData = {
        name: productFormData.value.name,
        description: productFormData.value.description,
        price: productFormData.value.price,
        discountPrice: productFormData.value.discountPrice,
        discountReason: productFormData.value.discountReason,
        contextUrl: productFormData.value.contextUrl,
        selectedInboxes: productFormData.value.selectedInboxes.includes('todos') ? null : productFormData.value.selectedInboxes,
        category: categoryNames,
        images: imageUrls,
        product_type: productFormData.value.productType === 'Físico' ? 'physical' : 'digital',
        // Campos específicos para produtos físicos
        ...(productFormData.value.productType === 'Físico' && {
          weightKg: productFormData.value.weightKg,
          dimensionsCm: dimensionsArray,
          color: productFormData.value.color,
          shipsTo: productFormData.value.shipsTo
        }),
        // Campos específicos para produtos digitais
        ...(productFormData.value.productType === 'Digital' && {
          fileFormat: productFormData.value.fileFormat,
          licenseType: productFormData.value.licenseType
        })
      };

      try {
        const response = await wootAPI.post(
          `/api/v1/catalogs`,
          { product: productData },
          { headers }
        );

        const savedProduct = response.data;
        emit('save', savedProduct);
        emit('success', savedProduct);
        resetForms();
        closeModal();
      } catch (err) {
        console.error('Falha em salvar produto.', err);
        // Check for specific file size error
        if (err.message === 'Arquivo muito grande. Máximo 5MB permitido') {
          error.value = 'Arquivo muito grande. Máximo 5MB permitido';
        } else {
          error.value = err.response?.data?.message || 'Erro interno do servidor. Tente novamente.';
        }
      } finally {
        loading.value = false;
      }
    }, 300);

    const saveService = debounce(async () => {
      if (!isServiceFormValid.value) return;

      loading.value = true;
      error.value = null;

      const headers = getAuthHeaders();
      if (!headers['access-token']) {
        error.value = 'Usuário não autenticado.';
        loading.value = false;
        return;
      }

      const categoryNames = serviceFormData.value.category.map(item =>
        typeof item === 'string' ? item : item.name
      );

      // Upload temporary images to Firebase before saving
      const imageUrls = await uploadTemporaryImages(serviceFormData.value.images);

      const serviceData = {
        name: serviceFormData.value.name,
        description: serviceFormData.value.description,
        price: serviceFormData.value.price,
        discountPrice: serviceFormData.value.discountPrice,
        discountReason: serviceFormData.value.discountReason,
        contextUrl: serviceFormData.value.link,
        selectedInboxes: serviceFormData.value.selectedInboxes.includes('todos') ? null : serviceFormData.value.selectedInboxes,
        product_type: serviceFormData.value.type,
        category: categoryNames,
        images: imageUrls,
        address: serviceFormData.value.address,
        // Campos específicos para delivery
        ...(serviceFormData.value.type === 'delivery' && {
          maxDistance: serviceFormData.value.maxDistance,
          isFixedPrice: serviceFormData.value.isFixedPrice,
          estimatedMinutes: serviceFormData.value.estimatedMinutes
        }),
        // Campos específicos para consulta, tratamento e aluguel
        ...(['consulta', 'tratamento', 'aluguel'].includes(serviceFormData.value.type) && {
          isHourlyRate: serviceFormData.value.isHourlyRate,
          advanceBookingDays: serviceFormData.value.advanceBookingDays
        }),
        // Specialty for consulta and tratamento
        ...(['consulta', 'tratamento'].includes(serviceFormData.value.type) && {
          specialty: serviceFormData.value.specialty
        }),
        // Duration for consulta and tratamento
        ...(['consulta', 'tratamento'].includes(serviceFormData.value.type) && {
          durationMinutes: serviceFormData.value.durationMinutes
        }),
        // Campos específicos para assinatura
        ...(serviceFormData.value.type === 'assinatura' && {
          planType: serviceFormData.value.planType
        })
      };

      try {
        const response = await wootAPI.post(
          `/api/v1/catalogs`,
          { product: serviceData },
          { headers }
        );

        const savedService = response.data;
        emit('save', savedService);
        emit('success', savedService);
        resetForms();
        closeModal();
      } catch (err) {
        console.error('Falha em salvar serviço.', err);
        // Check for specific file size error
        if (err.message === 'Arquivo muito grande. Máximo 5MB permitido') {
          error.value = 'Arquivo muito grande. Máximo 5MB permitido';
        } else {
          error.value = err.response?.data?.message || 'Erro interno do servidor. Tente novamente.';
        }
      } finally {
        loading.value = false;
      }
    }, 300);

    // Image compression utility function
    const compressImage = async (file, targetSizeBytes = 1024 * 1024) => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          console.log(`🖼️ Image loaded: ${file.name}, Original dimensions: ${img.width}x${img.height}`);
          
          // Calculate new dimensions while maintaining aspect ratio
          const maxWidth = 1920;
          const maxHeight = 1920;
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          console.log(`📐 Calculated dimensions for ${file.name}: ${width}x${height} (from ${img.width}x${img.height})`);
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress with different quality levels
          const tryCompress = (quality) => {
            console.log(`🔄 Attempting compression for ${file.name} with quality: ${quality}`);
            
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob((blob) => {
              if (blob && (blob.size <= targetSizeBytes || quality <= 0.3)) {
                console.log(`✅ Compression successful for ${file.name}: ${(blob.size / 1024 / 1024).toFixed(2)}MB at quality ${quality}`);
                
                // Create a new File object with the original filename
                const compressedFile = new File([blob], file.name, {
                  type: blob.type,
                  lastModified: Date.now()
                });
                
                // Clean up object URL
                const index = objectURLs.value.indexOf(img.src);
                if (index > -1) objectURLs.value.splice(index, 1);
                URL.revokeObjectURL(img.src);
                resolve(compressedFile);
              } else if (quality > 0.3) {
                console.log(`⚠️ File ${file.name} still too large (${(blob.size / 1024 / 1024).toFixed(2)}MB), reducing quality to ${(quality - 0.2).toFixed(1)}`);
                
                // Try with lower quality
                tryCompress(quality - 0.2);
              } else {
                console.log(`❌ Compression failed for ${file.name}, using original file`);
                
                // Clean up object URL
                const index = objectURLs.value.indexOf(img.src);
                if (index > -1) objectURLs.value.splice(index, 1);
                URL.revokeObjectURL(img.src);
                
                // If we can't compress enough, return original
                resolve(file);
              }
            }, file.type.startsWith('image/png') ? 'image/jpeg' : file.type, quality);
          };
          
          // Start with quality 0.8
          tryCompress(0.8);
        };
        
        img.onerror = () => {
          const index = objectURLs.value.indexOf(img.src);
          if (index > -1) objectURLs.value.splice(index, 1);
          URL.revokeObjectURL(img.src);
          resolve(file);
        };
        
        const objectURL = URL.createObjectURL(file);
        objectURLs.value.push(objectURL); // Track for cleanup
        img.src = objectURL;
      });
    };

    const triggerFileUpload = () => {
      if (fileInput.value) {
        fileInput.value.click();
      }
    };

    const handleFileUpload = async (event) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      loading.value = true;
      error.value = null;

      try {
        const filePromises = Array.from(files).map(async (file) => {
          if (!file.type.startsWith('image/')) {
            console.warn('Arquivo ignorado (não é imagem):', file.name);
            return null;
          }
          
          console.log(`📸 Original file: ${file.name}, Size: ${(file.size / 1024 / 1024).toFixed(2)}MB, Type: ${file.type}`);
          
          // Check if file needs compression (> 1MB)
          const oneMB = 1024 * 1024;
          const maxSizeInBytes = 30 * 1024 * 1024; // 30MB limit
          let processedFile = file;
          
          if (file.size > maxSizeInBytes) {
            throw new Error('Arquivo muito grande. Máximo 30MB permitido');
          }
          
          // Compress if file is larger than 1MB
          if (file.size > oneMB) {
            try {
              compressing.value = true;
              processedFile = await compressImage(file, oneMB);
              console.log(`Imagem comprimida: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
            } catch (compressionError) {
              console.warn('Falha na compressão, usando arquivo original:', compressionError);
              processedFile = file;
            } finally {
              compressing.value = false;
            }
          }
          
          // Store file temporarily as base64 for preview
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: Date.now().toString() + Math.random().toString(36),
                url: e.target.result, // base64 data URL for preview
                file: processedFile, // store processed file for later upload
                isTemporary: true // flag to identify temporary images
              });
            };
            reader.readAsDataURL(processedFile);
          });
        });

        const tempImages = (await Promise.all(filePromises)).filter(Boolean);
        
        // Add temporary images to the appropriate form data
        if (status.value === 'product') {
          productFormData.value.images.push(...tempImages);
        } else {
          serviceFormData.value.images.push(...tempImages);
        }

      } catch (err) {
        console.error('Erro ao processar imagens:', err);
        error.value = err.message || 'Erro ao processar as imagens.';
      } finally {
        loading.value = false;
        // Clear the file input so the same file can be selected again
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      }
    };

    const resetForms = () => {
      productFormData.value = {
        name: '',
        description: '',
        price: null,
        discountPrice: null,
        discountReason: '',
        contextUrl: '',
        productType: '',
        selectedInboxes: ['todos'],
        category: [],
        images: [],
        // Reset physical product fields
        weightKg: null,
        dimensionsCm: {
          height: null,
          width: null,
          depth: null
        },
        color: '',
        shipsTo: '',
        // Reset digital product fields
        fileFormat: '',
        licenseType: ''
      };
      serviceFormData.value = {
        name: '',
        description: '',
        price: null,
        discountPrice: null,
        discountReason: '',
        link: '',
        address: '',
        type: '',
        selectedInboxes: ['todos'],
        category: [],
        images: [],
        // Reset campos específicos para aluguel e delivery
        maxDistance: '',
        isFixedPrice: false,
        estimatedMinutes: null,
        // Reset campos específicos para consulta e tratamento
        isHourlyRate: false,
        advanceBookingDays: null,
        specialty: '',
        durationMinutes: null,
        // Reset campos específicos para assinatura
        planType: ''
      };
      productFormattedValue.value = '';
      productFormattedDiscountPrice.value = '';
      serviceFormattedValue.value = '';
      serviceFormattedDiscountPrice.value = '';
      selectedType.value = '';
      status.value = 'form';
      error.value = null;
    };

    onMounted(() => {
      loadInboxes();
      loadSpecialties();
      document.addEventListener('click', handleClickOutside);
      
      // Focus management: focus the modal when it opens
      nextTick(() => {
        const firstFocusable = document.querySelector('.modal-content [tabindex]:not([tabindex="-1"]), .modal-content button:not([disabled]), .modal-content input:not([disabled])');
        if (firstFocusable) {
          firstFocusable.focus();
        }
      });
    });

    // Store object URLs for cleanup
    const objectURLs = ref([]);

    // Add cleanup on component unmount
    onBeforeUnmount(() => {
      // Clean up any remaining object URLs
      objectURLs.value.forEach(url => URL.revokeObjectURL(url));
      objectURLs.value = [];
      
      // Remove event listeners
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      status,
      selectedType,
      loading,
      compressing,
      error,
      productFormData,
      serviceFormData,
      productFormattedValue,
      productFormattedDiscountPrice,
      serviceFormattedValue,
      serviceFormattedDiscountPrice,
      productDescriptionCharsRemaining,
      serviceDescriptionCharsRemaining,
      isProductFormValid,
      isServiceFormValid,
      selectType,
      proceedToForm,
      backToSelection,
      closeModal,
      handleProductValueInput,
      handleProductDiscountPriceInput,
      handleServiceValueInput,
      handleServiceDiscountPriceInput,
      handleBackdropClick,
      saveProduct,
      saveService,
      triggerFileUpload,
      handleFileUpload,
      fileInput,
      // Inbox functionality
      inboxOptions,
      showInboxDropdown,
      groupedInboxOptions,
      getInboxTypeIcon,
      toggleInboxDropdown,
      // Multi-select inbox functionality
      isInboxSelected,
      toggleInboxSelection,
      getInboxName,
      getInboxDisplayName,
      getSelectedInboxesDisplayText,
      getSelectedInboxesDisplayIcon,
      getSelectedInboxesDisplayClasses,
      // Specialties functionality
      specialties
    };
  }
};
</script>

<style scoped>
/* Chrome, Safari, Edge (WebKit/Blink) */
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}
</style>